import React, { useState, useEffect } from "react";
import { useAuth } from "../../AuthContext";
import { toast } from 'react-toastify';
import './Settings.css';
import configData from '../../configure';
import { setOrgUpiId, getOrgUpiId } from '../../config/orgUpiConfig';

const OrganizationSettings = ({ orgId: orgIdFromFlow }) => {
  const { axios, token, user, isValidated, orgId: contextOrgId, parentOrgId } = useAuth();
  const orgId = orgIdFromFlow || contextOrgId || user?.orgId || user?.organizationId || user?.org_id || null;

  const [formData, setFormData] = useState({
    orgName: "",
    address: "",
    city: "",
    state: "",
    pinCode: "",
    country: "India",
    email: "",
    phone: "",
    alternatePhoneNumber: "",
    imageUrl: "",
    orgStatus: "ACTIVE",
    upiId: "",
  });

  const [rawOrgData, setRawOrgData] = useState(null);
  const [originalData, setOriginalData] = useState(null); // for cancel
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!token || !isValidated || !orgId) {
      if (isValidated && !orgId) {
        toast.error("No organization ID found in your profile.");
      }
      return;
    }

    const fetchOrgDetails = async () => {
      setIsLoading(true);

      try {
        let response;
        try {
          response = await axios.get(
            configData.get_identity_identity_org_orgid(orgId, orgId),
            { headers: { accept: 'application/hal+json' } }
          );
        } catch (e1) {
          response = await axios.get(
            configData.get_identity_identity_org(orgId),
            { headers: { accept: 'application/hal+json' } }
          );
        }

        const data = response.data || {};
        setRawOrgData(data);

        const rawAddr = data.addressDetails || data.address;
        const addr = Array.isArray(rawAddr) ? (rawAddr[0] || {}) : (rawAddr || {});
        const contact = data.contact || data.contactDetails || {};

        const addressCombined = [addr.addressLine1, addr.addressLine2]
          .filter(Boolean)
          .map(s => String(s).trim())
          .filter(Boolean)
          .join(', ');

        const resolvedUpiId = data.customFields?.upiId || data.customFields?.upi_id || data.upiId || getOrgUpiId(orgId) || "";
        if (resolvedUpiId) {
          setOrgUpiId(orgId, resolvedUpiId);
        }

        const loadedData = {
          orgName: data.orgName || data.name || "",
          address: addressCombined || addr.address || "",
          city: addr.city || "",
          state: addr.state || "",
          pinCode: addr.pinCode || addr.pincode || addr.pin || "",
          country: addr.country || "India",
          email: contact.email || data.email || "",
          phone: contact.phone || data.phoneNumber || data.phone || "",
          alternatePhoneNumber: addr.alternatePhoneNumber || contact.alternatePhoneNumber || "",
          imageUrl: data.imageUrl ? data.imageUrl.trim() : "",
          orgStatus: data.orgStatus || data.status || "ACTIVE",
          upiId: resolvedUpiId,
        };

        setFormData(loadedData);
        setOriginalData(loadedData);
      } catch (err) {
        console.error("Organization fetch failed:", err);
        const msg = err.response?.data?.message || err.message || "Failed to load organization";
        toast.error(msg);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrgDetails();
  }, [token, isValidated, orgId, axios]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setFormData({ ...originalData });
    setIsEditMode(false);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!orgId) {
      toast.error("Cannot save: organization ID is missing.");
      return;
    }

    if (!formData.orgName.trim()) {
      toast.error("Organization Name is required.");
      return;
    }

    setIsLoading(true);

    const addressObj = {
      addressType: rawOrgData?.addressDetails?.addressType || "CURRENT",
      addressLine1: formData.address ? formData.address.trim() : null,
      addressLine2: rawOrgData?.addressDetails?.addressLine2 || null,
      city: formData.city ? formData.city.trim() : null,
      state: formData.state ? formData.state.trim() : null,
      country: formData.country ? formData.country.trim() : "India",
      pinCode: formData.pinCode ? formData.pinCode.trim() : null,
      alternatePhoneNumber: formData.alternatePhoneNumber ? formData.alternatePhoneNumber.trim() : null,
      latitude: rawOrgData?.addressDetails?.latitude || null,
      longitude: rawOrgData?.addressDetails?.longitude || null,
    };

    const contactObj = {
      email: formData.email ? formData.email.trim() : null,
      phone: formData.phone ? formData.phone.trim() : null,
    };

    // Strip HAL links/embedded metadata if present on rawOrgData
    const { _links, _embedded, ...cleanRawOrgData } = rawOrgData || {};

    const updatedCustomFields = {
      ...(cleanRawOrgData.customFields || {}),
      upiId: formData.upiId ? formData.upiId.trim() : null,
    };

    // Build a clean payload matching backend Organizations DTO schema
    const payload = {
      ...cleanRawOrgData,
      orgId: orgId,
      orgName: formData.orgName.trim(),
      parentOrgId: cleanRawOrgData.parentOrgId || parentOrgId || null,
      orgCode: cleanRawOrgData.orgCode || formData.orgName.trim().substring(0, 10).toUpperCase().replace(/\s+/g, '-') || orgId,
      orgType: cleanRawOrgData.orgType || "SCHOOL",
      orgStatus: formData.orgStatus || cleanRawOrgData.orgStatus || "ACTIVE",
      addressDetails: addressObj,
      contact: contactObj,
      imageUrl: formData.imageUrl ? formData.imageUrl.trim() : (cleanRawOrgData.imageUrl ? cleanRawOrgData.imageUrl.trim() : ""),
      customFields: updatedCustomFields,
      upiId: formData.upiId ? formData.upiId.trim() : null,
      lastUpdatedBy: user?.userId || user?.name || "system",
      lastUpdatedAt: new Date().toISOString(),
    };

    try {
      try {
        await axios.put(
          configData.get_identity_identity_org(orgId),
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/hal+json, application/json',
            },
          }
        );
      } catch (e1) {
        await axios.put(
          configData.get_identity_identity_org_orgid(orgId, orgId),
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/hal+json, application/json',
            },
          }
        );
      }

      if (formData.upiId) {
        setOrgUpiId(orgId, formData.upiId.trim());
      }

      toast.success("Organization settings saved successfully!");
      setRawOrgData(prev => ({ ...(prev || {}), ...payload }));
      setOriginalData({ ...formData });
      setIsEditMode(false);
    } catch (err) {
      console.error("Update failed:", err);
      const serverMsg = err.response?.data?.message || err.response?.data?.error || err.response?.data?.details || err.message || "Update failed";
      toast.error(`Save failed: ${serverMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormDisabled = !isEditMode || isLoading;

  return (
    <div className="settings-wrapper">
      <div className="settings-header">
        <h2 className="settings-title">Organization Settings</h2>
        {!isEditMode && !isLoading && (
          <button className="edit-mode-btn" onClick={handleEdit} title="Edit Settings">
            ✏️ Edit
          </button>
        )}
      </div>

      {isEditMode && (
        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </button>
          <button type="button" className="save-btn" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}

      {isLoading && <p className="loading">Loading...</p>}

      <form onSubmit={handleSubmit} className="settings-form">
        {/* Organization Name */}
        <div className="form-row">
          <div className="form-field" style={{ flex: 1 }}>
            <label>Organization Name *</label>
            <input
              type="text"
              name="orgName"
              value={formData.orgName}
              onChange={handleChange}
              placeholder="Enter Organization Name"
              required
              disabled={isFormDisabled}
            />
          </div>
        </div>



        {/* City and State */}
        <div className="form-row">
          <div className="form-field">
            <label>City</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-field">
            <label>State</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="State"
              disabled={isFormDisabled}
            />
          </div>
        </div>

        {/* Pin Code and Country */}
        <div className="form-row">
          <div className="form-field">
            <label>Pin Code</label>
            <input
              type="text"
              name="pinCode"
              value={formData.pinCode}
              onChange={handleChange}
              placeholder="Pin Code"
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-field">
            <label>Country</label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder="Country"
              disabled={isFormDisabled}
            />
          </div>
        </div>

        {/* Phone and Email */}
        <div className="form-row">
          <div className="form-field">
            <label>Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone Number"
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-field">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address"
              disabled={isFormDisabled}
            />
          </div>
        </div>

        {/* UPI ID */}
        <div className="form-row">
          <div className="form-field" style={{ flex: 1 }}>
            <label>UPI ID</label>
            <input
              type="text"
              name="upiId"
              value={formData.upiId}
              onChange={handleChange}
              placeholder="e.g. schoolname@upi"
              disabled={isFormDisabled}
            />
          </div>
        </div>

        {isEditMode && (
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-btn"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default OrganizationSettings;